import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories, getComicsByCategory } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "romance");
  const [sortBy, setSortBy] = useState<"popular" | "latest" | "rating">("popular");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const comics = getComicsByCategory(activeCategory);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === "rating") return b.rating - a.rating;
    if (sortBy === "latest") return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    return parseInt(b.views) - parseInt(a.views);
  });

  const categoryName = currentCategory?.name || "分类";

  return (
    <>
      <SEOHead
        title={`${categoryName}韩漫 - 免费${categoryName}韩国漫画在线阅读`}
        description={`韩漫之家${categoryName}分类，收录大量${categoryName}题材韩国漫画，支持下拉式免费在线阅读，每日更新最新章节。`}
        keywords={`${categoryName}韩漫, ${categoryName}韩国漫画, 免费${categoryName}漫画, 韩漫之家`}
        canonicalUrl={`/category/${activeCategory}`}
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              共 {comics.length} 部作品 · 每日更新 · 免费阅读
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: "popular", label: "最热门" },
                { key: "latest", label: "最新更新" },
                { key: "rating", label: "最高评分" },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortBy(option.key as typeof sortBy)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === option.key
                      ? "bg-secondary-200 text-secondary-800 font-medium"
                      : "bg-background-100 text-foreground-500 hover:bg-background-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {sortedComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden rounded-lg mb-3">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-full">
                          {comic.rating}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-[10px] text-white/80">{comic.status}</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-500">{comic.author}</span>
                      <span className="text-xs text-foreground-400">{comic.views}次</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-book-2-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画作品</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}